import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { put, select, takeLatest } from "redux-saga/effects";
import { call } from "typed-redux-saga";

import { callApi } from "../../sagas";
import State from "../../State";
import { Cognito } from "../../utils/cognito";
import errors from "../../utils/errors";
import intl from "../../utils/intl";
import startDownload from "../../utils/startDownload";
import { setError } from "../App/actions";
import * as actions from "./actions";
import { Agreement } from "./Agreement";
import {
  ACTION_CREATE_AGREEMENT,
  ACTION_DELETE_AGREEMENT,
  ACTION_DOWNLOAD_AGREEMENTS,
  ACTION_LOAD_AGREEMENTS,
  ACTION_SAVE_AGREEMENTS,
  ACTION_UPLOAD_AGREEMENTS,
} from "./constants";
import messages from "./messages";

interface ValidationResult {
  supplierAgreements: Array<Agreement>;
  errors: Array<string>;
}

interface CreateAgreementImportResponse {
  id: string;
  uploadUrl: string;
}

interface CreateDownloadFileResponse {
  downloadUrl: string;
}

const E100SupplierPartnerId = "E100";

const getAccessToken = (state: State): string =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  state.session.session!.credentials.accessToken;

function* loadAgreementsSaga(axiosClient: AxiosInstance, cognito: Cognito) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const response: AxiosResponse = yield callApi(
      cognito,
      accessToken,
      axiosClient.get,
      "/admin/agreements",
      requestConfig
    );

    yield put(
      actions.loadAgreementsComplete(
        response.data.map(
          (agreement: { cardIssuerId: string; supplierId: string }) =>
            new Agreement(agreement.cardIssuerId, agreement.supplierId)
        )
      )
    );
  } catch (error) {
    yield put(setError(error));
  }
}

export function* watchLoadAgreements(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  yield takeLatest(
    ACTION_LOAD_AGREEMENTS,
    loadAgreementsSaga,
    axiosClient,
    cognito
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createAgreementSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito,
  action: actions.CreateAgreementAction
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    yield callApi(
      cognito,
      accessToken,
      axiosClient.post,
      "/admin/agreements",
      {
        cardIssuerId: action.agreement.cardIssuerId,
        supplierId: action.agreement.supplierId,
        supplierPartnerId: E100SupplierPartnerId,
      },
      requestConfig
    );

    yield put(actions.createAgreementSuccess(action.agreement));
  } catch (e) {
    yield put(
      setError(
        `${intl.formatMessage(
          messages.unableToCreateAgreement
        )} ${errorToString(e)}`
      )
    );
    yield put(actions.createAgreementFailed());
  }
}

function errorToString(e: unknown): string {
  if (e instanceof AxiosError && e.response && e.response.status === 409) {
    return intl.formatMessage(messages.agreementConflict);
  } else {
    return errors.toString(e);
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* watchCreateAgreement(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeLatest(
      ACTION_CREATE_AGREEMENT,
      createAgreementSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}

export function* deleteAgreementSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito,
  action: actions.DeleteAgreementAction
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const data = {
      cardIssuerId: action.payload.cardIssuerId,
      supplierId: action.payload.supplierId,
      supplierPartnerId: E100SupplierPartnerId,
    };

    yield callApi(
      cognito,
      accessToken,
      axiosClient.post,
      "/admin/agreements/delete",
      data,
      requestConfig
    );

    yield put(actions.agreementDeleteSuccess());
  } catch (error) {
    yield put(actions.agreementDeleteFailed());
    yield put(setError(error));
  }
}

export function* watchDeleteAgreement(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeLatest(
      ACTION_DELETE_AGREEMENT,
      deleteAgreementSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}

export function* downloadAgreementsSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const response: AxiosResponse<CreateDownloadFileResponse> = yield callApi(
      cognito,
      accessToken,
      axiosClient.post,
      "/admin/agreements/export",
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    yield put(actions.downloadAgreementsStarted());

    yield call(startDownload, response.data.downloadUrl);
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to download agreements: ${message}`));
  }
}

export function* watchDownloadAgreements(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeLatest(
      ACTION_DOWNLOAD_AGREEMENTS,
      downloadAgreementsSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* uploadAgreementsSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito,
  action: actions.UploadAgreementsAction
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const createImportResponse: AxiosResponse<CreateAgreementImportResponse> =
      yield callApi(
        cognito,
        accessToken,
        axiosClient.post,
        "/admin/agreements/import",
        null,
        requestConfig
      );

    yield call(
      axiosClient.put,
      createImportResponse.data.uploadUrl,
      action.content
    );
    const validateImportResponse: AxiosResponse<ValidationResult> =
      yield callApi(
        cognito,
        accessToken,
        axiosClient.post,
        `/admin/agreements/import/${createImportResponse.data.id}/validate`,
        null,
        requestConfig
      );
    if (validateImportResponse.data.errors.length === 0) {
      yield put(
        actions.agreementsUploadSucceeded(
          validateImportResponse.data.supplierAgreements,
          createImportResponse.data.id
        )
      );
    } else {
      yield put(
        actions.agreementsUploadFailed(validateImportResponse.data.errors)
      );
    }
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to upload agreements: ${message}`));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* watchUploadAgreements(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeLatest(
      ACTION_UPLOAD_AGREEMENTS,
      uploadAgreementsSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* saveAgreementsSaga(
  axiosClient: AxiosInstance,
  cognito: Cognito,
  action: actions.SaveAgreementsAction
) {
  try {
    const accessToken: string = yield select(getAccessToken);

    yield callApi(
      cognito,
      accessToken,
      axiosClient.post,
      `/admin/agreements/import/${action.agreementImportId}/save`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    yield put(actions.agreementsSaved());
    yield put(actions.loadAgreements());
  } catch (e) {
    const message = errors.toString(e);
    yield put(setError(`unable to save agreements: ${message}`));
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* watchSaveAgreements(
  axiosClient: AxiosInstance,
  cognito: Cognito
) {
  try {
    yield takeLatest(
      ACTION_SAVE_AGREEMENTS,
      saveAgreementsSaga,
      axiosClient,
      cognito
    );
  } catch (error) {
    yield put(setError(error));
  }
}
